const CAP_BITCOIN_HASH_FULL = {
  title: "El Hash y la Función Hash en Bitcoin",
  sections: [
    // =========================
    // Curso Bitcoin 101 – El Hash
    // =========================
    {
      heading: "¿Qué es un hash criptográfico?",
      text: [
        "Llamamos hash al resultado de aplicar una función criptográfica que toma unos datos de entrada y los transforma en una cadena alfanumérica única de longitud fija.",
        "Da igual cuántos datos se introduzcan: el digest o huella generada siempre tendrá el mismo tamaño. Estas funciones se usan para asegurar la autenticidad de la información, almacenar contraseñas de forma segura y firmar documentos electrónicos."
      ],
      bluepoint: [
        " **Entrada arbitraria, salida fija:** el hash siempre tiene el mismo tamaño aunque el mensaje cambie de longitud.",
        " **Determinista:** el mismo mensaje produce siempre el mismo hash.",
        " **Unidireccional:** no es posible recuperar el mensaje original a partir del hash.",
        " **Alta sensibilidad:** un mínimo cambio en el texto genera un hash totalmente distinto.",
        " **Uso generalizado:** se utiliza en contraseñas, firmas digitales, certificados y, por supuesto, en blockchain."
      ]
    },
    {
      heading: "Funciones hash en el universo Blockchain",
      text: [
        "Las funciones hash son piezas clave en la tecnología blockchain porque son rápidas, eficientes, económicas en términos de cómputo y generan resultados únicos.",
        "Satoshi Nakamoto explicó en el whitepaper de Bitcoin por qué y cómo se usan hash como SHA-256 y RIPEMD-160 dentro del protocolo. Aunque las blockchains han evolucionado, la base sigue siendo la misma: utilizar criptografía fuerte y hashes para lograr seguridad, privacidad y, en muchos casos, cierto grado de anonimato."
      ],
      bluepoint: [
        " **Seguridad estructural:** los hashes garantizan que los bloques y transacciones no se puedan modificar sin ser detectados.",
        " **Privacidad mejorada:** permiten representar información sensible sin exponerla directamente.",
        " **Eficiencia:** son rápidos de calcular y fáciles de verificar por cualquier nodo.",
        " **Estándares robustos:** SHA-256 y RIPEMD-160 son ejemplos de funciones de hash ampliamente auditadas.",
        " **Base de muchas capas:** se usan en direcciones, minería, contratos inteligentes, árboles de Merkle, etc."
      ]
    },
    {
      heading: "Diagrama: ejemplos de función hash",
      image: {
        src: "https://raw.githubusercontent.com/AppsDevsLeon/ImagesBooks/refs/heads/main/imagesFundamentosBlockchain/Biucoinmaster/funcion_hash_criptomonedas.png",
        alt: "Diagrama de entradas de texto que pasan por una función hash criptográfica y producen diferentes digests",
        caption:
          "Cada línea muestra cómo pequeños cambios en el texto de entrada producen hashes totalmente distintos, aunque la función criptográfica y el tamaño de la salida sean siempre los mismos."
      },
      text: [
        "Te recomendamos observar el diagrama mientras lees este capítulo. A la izquierda se muestran distintas frases de entrada; en el centro, la función hash criptográfica; a la derecha, los digests o resultados.",
        "La idea clave es que cambios mínimos en el mensaje provocan cambios radicales en el hash, lo que hace muy difícil falsificar datos sin que se note."
      ]
    },
    {
      heading: "Creación de la dirección (Address / Wallet)",
      text: [
        "Las direcciones de los monederos de criptomonedas son una representación segura y más corta de las claves públicas de la cartera.",
        "Las claves públicas suelen ser largas y complejas, por lo que se aplican funciones hash en varias etapas para obtener una dirección más manejable y con capas adicionales de seguridad."
      ],
      bluepoint: [
        " **Claves públicas muy largas:** poco prácticas para usarlas directamente como identificador.",
        " **Derivación mediante hash:** se aplican funciones como SHA-256 y RIPEMD-160 para obtener direcciones más cortas.",
        " **Varias rondas de hash:** el proceso se repite varias veces para reforzar la seguridad.",
        " **Unicidad:** cada clave pública válida deriva en una dirección única.",
        " **Protección extra:** se añaden mecanismos como el checksum para detectar errores o manipulaciones."
      ]
    },
    {
      heading: "Hash y proceso de minería",
      text: [
        "La minería es otra etapa fundamental de la tecnología blockchain donde se usan funciones hash de forma intensiva.",
        "En Bitcoin, los mineros calculan millones de hashes SHA-256 por segundo para intentar encontrar un bloque válido, al mismo tiempo que verifican las transacciones que se emiten en la red."
      ],
      bluepoint: [
        " **Uso intensivo de SHA-256:** cada intento de bloque implica calcular un nuevo hash.",
        " **Prueba de trabajo (Proof-of-Work):** encontrar un hash con un patrón concreto demuestra que se ha invertido poder de cómputo.",
        " **Recompensa económica:** al resolver el acertijo, el minero recibe BTC nuevos más las comisiones de las transacciones.",
        " **Seguridad de la red:** el alto coste de ataque hace que sea muy difícil reescribir la historia de la blockchain.",
        " **Verificación sencilla:** aunque minar es costoso, verificar un bloque es relativamente rápido para los nodos."
      ]
    },
    {
      heading: "Hashes en contratos inteligentes (Smart Contracts)",
      text: [
        "Los contratos inteligentes en redes como Bitcoin (a través de scripts), Ethereum, NEO o TRON utilizan funciones hash para gestionar información sensible y mantener la lógica de las aplicaciones.",
        "Muchas veces no es práctico ni seguro guardar todos los datos dentro de la blockchain, por lo que se almacenan hashes que actúan como referencia y prueba de integridad."
      ],
      bluepoint: [
        " **Protección de datos sensibles:** nombres, direcciones físicas o datos de terceros pueden representarse mediante hashes sin mostrarse abiertamente.",
        " **Versionado de contratos:** cada versión de un contrato produce un hash distinto, lo que permite identificar con precisión qué versión está activa.",
        " **Autenticidad:** el hash de un contrato sirve como huella digital que demuestra que el texto no ha sido alterado.",
        " **Ejemplo práctico:** en la venta de una casa pagada en bitcoin, el contrato y su hash pueden servir como prueba inalterable del acuerdo.",
        " **Enlaces off-chain:** se guarda en la blockchain el hash y fuera de la cadena el documento completo, manteniendo trazabilidad sin saturar la red."
      ]
    },

    // ====================================
    // Curso Avanzado Bitcoin – La Función Hash
    // ====================================
    {
      heading: "Función hash y seguridad informática",
      text: [
        "En seguridad informática, las funciones hash son esenciales para garantizar la integridad y autenticidad de la información que requiere altos niveles de protección.",
        "Se emplean en sistemas de validación y autenticación de usuarios, firma de documentos, gestión de certificados y en criptomonedas para evitar la falsificación de transacciones y acciones maliciosas."
      ],
      bluepoint: [
        " **Integridad:** permiten comprobar que los datos no han sido modificados.",
        " **Autenticación:** ayudan a validar identidades y credenciales.",
        " **Firmas digitales:** forman parte del proceso de firmado y verificación.",
        " **Contraseñas seguras:** se almacenan hashes de contraseñas en lugar de las contraseñas en texto plano.",
        " **Criptomonedas:** sirven como barrera contra intentos de manipular transacciones."
      ]
    },
    {
      heading: "Definición técnica de una función hash",
      text: [
        "Una función hash es un procedimiento criptográfico que, mediante un algoritmo específico, transforma un mensaje de entrada en una secuencia alfanumérica única de longitud fija llamada hash.",
        "No se trata de un cifrado reversible, sino de un resumen unidireccional: no se puede recuperar el mensaje original a partir del hash final. La única forma práctica de encontrar un mensaje concreto es probando combinaciones hasta que el hash generado coincida con el que se busca."
      ],
      bluepoint: [
        " **Resumen unidireccional:** del hash no se puede obtener el mensaje original.",
        " **Colisiones poco probables:** es extremadamente difícil encontrar dos mensajes distintos con el mismo hash.",
        " **Resistencia a ataques:** deben ser robustas frente a fuerza bruta y ataques criptográficos.",
        " **Comparación eficiente:** basta con comparar hashes en lugar de comparar documentos completos.",
        " **Base de muchos protocolos:** TLS, firmas digitales, certificados, blockchain, etc."
      ]
    },
    {
      heading: "Función hash en Bitcoin",
      text: [
        "Uno de los algoritmos de función hash más utilizados es SHA-256, muy extendido por su equilibrio entre seguridad y coste computacional.",
        "En Bitcoin se emplea en multitud de puntos del protocolo, desde la construcción de bloques hasta la generación de direcciones y el cálculo de árboles de Merkle."
      ],
      bluepoint: [
        " **SHA-256 como estándar:** ofrece un buen equilibrio entre seguridad, rendimiento y consumo energético.",
        " **Uso ubicuo:** aparece en direcciones, bloques, minería y estructuras internas.",
        " **Mayor seguridad con complejidad:** cuanto más complejo el hash y más tiempo lleva calcularlo, más costoso es atacar la red.",
        " **Parte del ADN de Bitcoin:** sin funciones hash robustas, el protocolo no podría garantizar integridad ni consenso.",
        " **Actualizaciones futuras:** otros esquemas pueden convivir, pero SHA-256 sigue siendo la base del sistema."
      ]
    },
    {
      heading: "Creación de direcciones: enfoque avanzado",
      text: [
        "En Bitcoin, la generación de una dirección utiliza varias veces la función SHA-256 y otras funciones hash relacionadas.",
        "Este proceso permite acortar la representación de la clave pública, añadir caracteres de control y dotar de coherencia al formato de la dirección."
      ],
      bluepoint: [
        " **Múltiples rondas de SHA-256:** la función puede aplicarse hasta tres veces para distintos pasos de la construcción de la dirección.",
        " **Dirección más corta que la clave pública:** mejora la usabilidad sin sacrificar seguridad.",
        " **Checksum integrado:** se añaden caracteres de control para detectar errores de escritura o transmisión.",
        " **Consistencia de formato:** todas las direcciones respetan un patrón que facilita su validación.",
        " **Compatibilidad con diferentes tipos de dirección:** Legacy, SegWit, Bech32, etc."
      ]
    },
    {
      heading: "Minería y Proof-of-Work: visión detallada",
      text: [
        "En la minería de Bitcoin, las funciones hash son el corazón del mecanismo de Proof-of-Work.",
        "El minero agrupa transacciones, construye el Árbol de Merkle, incorpora el hash del bloque anterior y un valor aleatorio llamado nonce. Después, aplica la función hash hasta que el resultado cumple un patrón exigente de dificultad."
      ],
      bluepoint: [
        " **Árbol de Merkle:** usa hashes para agrupar y representar todas las transacciones de un bloque en una sola raíz.",
        " **Nonce:** valor que el minero modifica continuamente para generar nuevos hashes.",
        " **Objetivo de dificultad:** el hash resultante debe tener un patrón concreto (por ejemplo, cierto número de ceros iniciales).",
        " **Millones de intentos:** hacer el hash una vez es fácil; hacerlo millones de veces requiere gran poder de cómputo, como repetir una sentadilla millones de veces.",
        " **Verificación ligera:** los nodos solo rehacen el cálculo una vez para comprobar que el bloque cumple las reglas.",
        " **Cadena unida:** al encadenar el hash de cada bloque con el anterior se consigue inmutabilidad y coherencia histórica."
      ]
    }
  ]
};

export default CAP_BITCOIN_HASH_FULL;
